/**
 * Returns identifier props that can connect an input with its label in an accessibility-valid
 * manner.
 *
 * The desired DOM structure is:
 *
 * ```tsx
 * <label id="my-input-label" htmlFor="my-input">My Input</label>
 * <input id="my-input" aria-labelledby="my-input-label" />
 * ```
 *
 * Typically only `input#id` and `label#htmlFor` are necessary, but some of our `input`s use
 * external libraries under the hood that need to be explicitly informed of the label's existence,
 * lest they log noisy console warnings at runtime (React Aria is a particular offender here).
 *
 * This function can be used to generate the `aria-labelledby` prop for the `input` based on the
 * `id` and `htmlFor` props of the `label`. To do this, it also requires that the `label` have an
 * `id`.
 *
 * @example
 * ```tsx
 * // Label
 * const labelId = getAccessibleInputLabelId({ inputId: "my-input" });
 * <label id={labelId} htmlFor={inputId}>My Input</label>
 *
 * // Input
 * <input id={inputId} aria-labelledby={labelId} />
 * ```
 * @param inputId - The input `id`.
 * @returns The props for an accessible label for an input.
 */
export function getAccessibleInputLabelId({ inputId }: { inputId: string }) {
  // eslint-disable-next-line lingui/no-unlocalized-strings
  return `${inputId}-label`;
}
